import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { FormEvent, useEffect, useState } from "react";
import { useUpdateTaskProgram } from "src/hooks/queries/task-program-mutations";
import { TaskProgram, TrainingQuizQuestion } from "src/services/backend";
import {
  TaskProgramSettingsFields,
  TaskProgramSettingsValues,
} from "./TaskProgramFormFields";
import { TrainingQuizAdminSection } from "./TrainingQuizAdminSection";

const programToForm = (program: TaskProgram): TaskProgramSettingsValues => ({
  name: program.name,
  description: program.description ?? "",
  enabled: program.enabled,
  completionMethod: program.completionMethod ?? "ACKNOWLEDGEMENT",
  dueAfterDays: program.dueAfterDays ?? null,
});

type TaskProgramFormProps = {
  orgId: string;
  program: TaskProgram;
  onCancel: () => void;
  onSaved: (program: TaskProgram) => void;
  onGenerateQuiz?: (existingQuestions?: TrainingQuizQuestion[]) => void;
  quizAdministration?: {
    documentId: string;
    documentVersionId: string;
    revision: number;
    isRevisionLive?: boolean;
  };
  historicalQuizRevisions?: Array<{
    documentVersionId: string;
    revision: number;
  }>;
  quizUnavailableMessage?: string;
  showQuizAdministration?: boolean;
};

export const TaskProgramForm = ({
  orgId,
  program,
  onCancel,
  onSaved,
  onGenerateQuiz,
  quizAdministration,
  historicalQuizRevisions,
  quizUnavailableMessage,
  showQuizAdministration = true,
}: TaskProgramFormProps) => {
  const updateMutation = useUpdateTaskProgram({ orgId });
  const [form, setForm] = useState<TaskProgramSettingsValues>(() =>
    programToForm(program)
  );
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setForm(programToForm(program));
    setFormError(null);
  }, [program]);

  const saveProgram = async (values = form): Promise<TaskProgram | null> => {
    setFormError(null);
    if (!values.name.trim()) {
      setFormError("Name is required.");
      return null;
    }

    return updateMutation.mutateAsync({
      programId: program.id,
      data: {
        name: values.name.trim(),
        description: values.description.trim() || null,
        enabled: values.enabled,
        ...(values.completionMethod !==
          (program.completionMethod ?? "ACKNOWLEDGEMENT") && {
          completionMethod: values.completionMethod,
        }),
        dueAfterDays: values.dueAfterDays,
      },
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const saved = await saveProgram();
    if (saved) onSaved(saved);
  };

  const handleGenerateQuiz = async (
    existingQuestions?: TrainingQuizQuestion[]
  ) => {
    const saved = await saveProgram();
    if (saved) onGenerateQuiz?.(existingQuestions);
  };

  const handleFormChange = (values: TaskProgramSettingsValues) => {
    const quizSelected =
      form.completionMethod !== "QUIZ" && values.completionMethod === "QUIZ";
    setForm(values);
    if (
      showQuizAdministration &&
      quizSelected &&
      quizAdministration &&
      onGenerateQuiz
    ) {
      void saveProgram(values).then((saved) => {
        if (saved) onGenerateQuiz();
      });
    }
  };

  const completionMethodChangedToQuiz =
    form.completionMethod === "QUIZ" &&
    (program.completionMethod ?? "ACKNOWLEDGEMENT") !== "QUIZ";

  return (
    <Box
      component="form"
      aria-label="Training settings"
      onSubmit={handleSubmit}
    >
      <Stack spacing={3}>
        <TaskProgramSettingsFields
          linkedDocumentTitle={program.linkedDocument.title}
          values={form}
          onChange={handleFormChange}
          completionMethodHelperText={
            completionMethodChangedToQuiz
              ? "If the current revision has no completed training, its unfinished tasks will be replaced by quiz training."
              : "Changes apply only to future training occurrences."
          }
        >
          {showQuizAdministration &&
            form.completionMethod === "QUIZ" &&
            quizAdministration && (
              <TrainingQuizAdminSection
                orgId={orgId}
                programId={program.id}
                documentId={quizAdministration.documentId}
                documentVersionId={quizAdministration.documentVersionId}
                revision={quizAdministration.revision}
                isRevisionLive={quizAdministration.isRevisionLive}
                historicalQuizRevisions={historicalQuizRevisions}
                onOpenAgent={handleGenerateQuiz}
              />
            )}

          {showQuizAdministration &&
            form.completionMethod === "QUIZ" &&
            !quizAdministration &&
            quizUnavailableMessage && (
              <Alert severity="info">{quizUnavailableMessage}</Alert>
            )}
        </TaskProgramSettingsFields>

        {formError && <Typography color="error">{formError}</Typography>}

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ pt: 1 }}
        >
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isPending || !form.name.trim()}
          >
            Update
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
